'use strict';

/**
 * Customer tracker data hook: reuses the production `usePipelineStatus` path
 * (RTK Query → mock/live baseQuery → snapshot mapping → current-flow selection →
 * graph adapter) and rolls the results into customer models via
 * {@link buildPipelineProgressBundleFromSnapshot}.
 */

import { useMemo } from 'react';
import { USE_PIPELINE_STATUS_API_MOCKS } from '@/config/env.config';
import {
	SINGLE_V7_PROJECT_FLOW_SCENARIO,
} from './pipelineStatusFlowScenarios';
import {
	pipelineProgressKindForMockScenario,
	requirePipelineStatusMockScenarioFromEnv,
} from './mocks/pipelineStatusFacts.mock';
import { usePipelineStatus, useResolvedPipelineStatusFlowScenario } from './usePipelineStatus';
import { buildPipelineProgressBundleFromSnapshot } from './pipelineProgressFromSnapshot';

/**
 * Customer presentation for a project pipeline.
 * @param {unknown} projectPipeline
 * @param {{ hasDiscoveryPanels?: boolean }} [options]
 * @returns {'v7' | 'core' | 'discoveryPanels' | 'kits' | 'merged' | 'unsupported'}
 */
export function pipelineProgressKind(projectPipeline, options) {
	if (USE_PIPELINE_STATUS_API_MOCKS) {
		return pipelineProgressKindForMockScenario(
			requirePipelineStatusMockScenarioFromEnv(projectPipeline),
		);
	}
	if (options?.hasDiscoveryPanels) return 'discoveryPanels';
	const p = String(projectPipeline ?? '').trim().toLowerCase();
	if (p === 'v7') return 'v7';
	if (p === 'core') return 'core';
	if (p === 'discoverypanels') return 'discoveryPanels';
	if (p === 'kits') return 'kits';
	if (p === 'merged') return 'merged';
	return 'unsupported';
}

/**
 * Whether the customer tracker supports this project's pipeline.
 * @param {unknown} projectPipeline
 * @param {{ hasDiscoveryPanels?: boolean }} [options]
 */
export function isPipelineProgressSupported(projectPipeline, options) {
	return pipelineProgressKind(projectPipeline, options) !== 'unsupported';
}

/**
 * @param {string | undefined} projectId
 * @param {unknown} projectPipeline Portal `ProjectDescription.pipeline`
 * @param {{ hasDiscoveryPanels?: boolean }} [options]
 */
export function usePipelineProgress(projectId, projectPipeline, options) {
	const progressOptions = useMemo(
		() => ({ hasDiscoveryPanels: options?.hasDiscoveryPanels }),
		[options?.hasDiscoveryPanels],
	);
	const kind = useMemo(
		() => pipelineProgressKind(projectPipeline, progressOptions),
		[projectPipeline, progressOptions],
	);
	// Kits / Merged load facts for the sample-set / shell trackers.
	const fetchPipelineStatus = kind !== 'unsupported';
	const flowScenario = useResolvedPipelineStatusFlowScenario(projectPipeline);

	const pipelineStatus = usePipelineStatus(
		fetchPipelineStatus ? projectId : undefined,
		fetchPipelineStatus ? flowScenario : SINGLE_V7_PROJECT_FLOW_SCENARIO,
		projectPipeline,
	);

	const bundle = useMemo(
		() => buildPipelineProgressBundleFromSnapshot({
			kind,
			flowScenarioKey: fetchPipelineStatus ? flowScenario : SINGLE_V7_PROJECT_FLOW_SCENARIO,
			facts: pipelineStatus.facts,
			iterationManifests: pipelineStatus.iterationManifests,
			definition: pipelineStatus.definition,
			currentFlows: pipelineStatus.currentFlows,
			projectId,
		}),
		[
			kind,
			flowScenario,
			fetchPipelineStatus,
			pipelineStatus.facts,
			pipelineStatus.iterationManifests,
			pipelineStatus.definition,
			pipelineStatus.currentFlows,
			projectId,
		],
	);

	return {
		kind: bundle.kind,
		model: bundle.model,
		coreModel: bundle.coreModel,
		discoveryModel: bundle.discoveryModel,
		kitsModel: bundle.kitsModel,
		stages: bundle.stages,
		definition: bundle.definition,
		facts: bundle.facts,
		isLoading: fetchPipelineStatus ? pipelineStatus.isLoading : false,
		isFetching: fetchPipelineStatus ? pipelineStatus.isFetching : false,
		error: fetchPipelineStatus ? pipelineStatus.error : undefined,
		refetch: pipelineStatus.refetch,
	};
}
